import { create } from 'zustand';

export interface Conversation {
  _id: string;
  conversationId: string;
  contactId: string;
  phoneNumber: string;
  status: 'active' | 'archived' | 'closed' | 'spam';
  priority: 'low' | 'normal' | 'high';
  category?: string;
  tags: string[];
  messageCount: number;
  unreadCount: number;
  lastMessageAt: string;
  lastMessageType?: string;
  lastMessageContent?: string;
  contactName?: string;
  isOnline?: boolean;
  lastSeen?: string;
  assignedTo?: string;
  assignmentStatus?: 'active' | 'inProgress' | 'locked' | 'completed' | 'cancelled' | 'transferred';
  lockedByName?: string;
  assignmentId?: string;
  slaBreached?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChatState {
  conversations: Conversation[];
  isLoading: boolean;
  isRefreshing: boolean;
  searchQuery: string;
  filter: 'all' | 'active' | 'archived';

  // Global refresh token
  conversationsRefreshToken: number;
  
  // Notification tracking
  pendingNotifications: Array<{
    id: string;
    conversationId: string;
    contactName: string;
    message: string;
    timestamp: string;
  }>;
  
  // Computed values
  getUnreadConversationsCount: () => number;
  
  // Actions
  setConversations: (conversations: Conversation[]) => void;
  addConversation: (conversation: Conversation) => void;
  updateConversation: (conversationId: string, updates: Partial<Conversation>) => void;
  removeConversation: (conversationId: string) => void;
  setLoading: (loading: boolean) => void;
  setRefreshing: (refreshing: boolean) => void;
  setSearchQuery: (query: string) => void;
  setFilter: (filter: 'all' | 'active' | 'archived') => void;
  
  // Real-time updates
  updateConversationLastMessage: (conversationId: string, lastMessage: string, timestamp: string) => void;
  incrementMessageCount: (conversationId: string) => void;
  incrementUnreadCount: (conversationId: string) => void;
  markMessagesAsRead: (conversationId: string) => void;
  
  // Notification management
  addPendingNotification: (conversationId: string, contactName: string, message: string, timestamp: string) => void;
  removePendingNotification: (notificationId: string) => void;
  clearPendingNotifications: () => void;
  
  // Clear state
  clearChatState: () => void;

  // Refresh actions
  bumpConversationsRefreshToken: () => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  // Initial state
  conversations: [],
  isLoading: false,
  isRefreshing: false,
  searchQuery: '',
  filter: 'all',
  conversationsRefreshToken: 0,
  
  // Notification tracking
  pendingNotifications: [],
  
  // Actions
  setConversations: (conversations) => {
    set({ conversations });
  },
  
  addConversation: (conversation) => set((state) => ({
    conversations: [conversation, ...state.conversations]
  })),
  
  updateConversation: (conversationId, updates) => set((state) => ({
    conversations: state.conversations.map(conv => 
      conv.conversationId === conversationId 
        ? { ...conv, ...updates }
        : conv
    )
  })),
  
  removeConversation: (conversationId) => set((state) => ({
    conversations: state.conversations.filter(conv => conv.conversationId !== conversationId)
  })),
  
  setLoading: (loading) => set({ isLoading: loading }),
  
  setRefreshing: (refreshing) => set({ isRefreshing: refreshing }),
  
  setSearchQuery: (query) => set({ searchQuery: query }),
  
  setFilter: (filter) => set({ filter }),
  
  // Real-time updates
  updateConversationLastMessage: (conversationId, lastMessage, timestamp) => set((state) => ({
    conversations: state.conversations.map(conv => 
      conv.conversationId === conversationId 
        ? { 
            ...conv, 
            lastMessageContent: lastMessage, 
            lastMessageAt: timestamp,
            updatedAt: new Date().toISOString()
          }
        : conv
    )
  })),
  
  incrementMessageCount: (conversationId) => set((state) => ({
    conversations: state.conversations.map(conv => 
      conv.conversationId === conversationId 
        ? { ...conv, messageCount: conv.messageCount + 1 }
        : conv
    )
  })),
  
  incrementUnreadCount: (conversationId) => {
    console.log('🔍 [ChatStore] incrementUnreadCount called for:', conversationId);
    set((state) => {
      const newConversations = [...state.conversations];
      const index = newConversations.findIndex(conv => conv.conversationId === conversationId);
      if (index !== -1) {
        newConversations[index] = {
          ...newConversations[index],
          unreadCount: (newConversations[index].unreadCount || 0) + 1
        };
        console.log('🔍 [ChatStore] Updated conversation unreadCount:', newConversations[index].unreadCount);
      } else {
        console.log('🔍 [ChatStore] Conversation not found:', conversationId);
      }
      return { conversations: newConversations };
    });
  },
  
  markMessagesAsRead: (conversationId) => {
    console.log('🔍 [ChatStore] markMessagesAsRead called for conversation:', conversationId);
    set((state) => {
      const newConversations = [...state.conversations];
      console.log('🔍 [ChatStore] Available conversations in store:', newConversations.map(conv => ({
        conversationId: conv.conversationId,
        contactName: conv.contactName,
        unreadCount: conv.unreadCount
      })));
      
      const index = newConversations.findIndex(conv => conv.conversationId === conversationId);
      console.log('🔍 [ChatStore] Found conversation at index:', index);
      if (index !== -1) {
        console.log('🔍 [ChatStore] Previous unreadCount:', newConversations[index].unreadCount);
        newConversations[index] = {
          ...newConversations[index],
          unreadCount: 0
        };
        console.log('🔍 [ChatStore] Updated unreadCount to 0 for conversation:', conversationId);
      } else {
        console.log('❌ [ChatStore] Conversation not found:', conversationId);
        console.log('🔍 [ChatStore] Trying alternative search by _id...');
        const altIndex = newConversations.findIndex(conv => conv._id === conversationId);
        if (altIndex !== -1) {
          console.log('🔍 [ChatStore] Found conversation by _id at index:', altIndex);
          newConversations[altIndex] = {
            ...newConversations[altIndex],
            unreadCount: 0
          };
          console.log('🔍 [ChatStore] Updated unreadCount to 0 for conversation by _id:', conversationId);
        } else {
          console.log('❌ [ChatStore] Conversation not found by _id either:', conversationId);
        }
      }
      return { conversations: newConversations };
    });
  },
  
  // Notification management
  addPendingNotification: (conversationId, contactName, message, timestamp) => set((state) => ({
    pendingNotifications: [...state.pendingNotifications, { id: Date.now().toString(), conversationId, contactName, message, timestamp }]
  })),
  
  removePendingNotification: (notificationId) => set((state) => ({
    pendingNotifications: state.pendingNotifications.filter(n => n.id !== notificationId)
  })),
  
  clearPendingNotifications: () => set({ pendingNotifications: [] }),

  bumpConversationsRefreshToken: () => {
    set((state) => ({ conversationsRefreshToken: state.conversationsRefreshToken + 1 }));
  },
  
  // Computed values
  getUnreadConversationsCount: () => {
    const state = get();
    
    // Simple filtering without excessive logging
    const conversationsWithUnread = state.conversations.filter(conv => (conv.unreadCount || 0) > 0);
    const unreadCount = conversationsWithUnread.length;
    
    // Only log when count changes significantly
    if (unreadCount !== (state as any)._lastUnreadCount) {
      console.log('🔍 [ChatStore] Unread count changed:', {
        totalConversations: state.conversations.length,
        unreadCount,
        previousCount: (state as any)._lastUnreadCount || 0
      });
      (state as any)._lastUnreadCount = unreadCount;
    }
    
    return unreadCount;
  },
  
  // Clear state
  clearChatState: () => set({
    conversations: [],
    isLoading: false,
    isRefreshing: false,
    searchQuery: '',
    filter: 'all',
    pendingNotifications: []
  })
}));
